import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useColor } from '@common-vue/media-card-large/src/modules/color';
import useSelectedMemberId from '@modules/latchkey/composables/useSelectedMemberId';
import { useProduct } from '@modules/product';
import { useProductFlags, useKillswitches } from '@modules/featureControl';
import { useProductType } from '@composables/useProductType';
import { useBrand } from '@composables/useBrand';
import { getUserIds } from '@utils/cookiesUtils';

/**
 * Collects key props from dynamic sources and return them in one single data object.
*/
export function useOutfittingData() {
  const store = useStore();
  const { userId } = getUserIds();
  const { isMcom } = useBrand();
  const { productData, productId } = useProduct();
  const {
    isMasterProductFlag, beautyFlag, homeFOBFlag, isConsolidatedThirdVariantFlag,
  } = useProductFlags();
  const { isConsolidatedPage } = useProductType();

  const { selectedColor } = useColor(productId);
  const { killswitches } = useKillswitches();

  const selectedProductId = useSelectedMemberId();

  const outfittingProps = computed(() => ({
    userId,
    brand: isMcom ? 'mcom' : 'bcom',
    pageType: 'pdp',
    // TODO: Temporary fix for the outfitting widget; it is expected to be reactive.
    // Remove this once the widget is updated to accept a non-reactive prop.
    productData: ref(productData),
    // value is not nullish, although it could be falsy
    ...(!!isConsolidatedThirdVariantFlag && { selectedProductId: selectedProductId.value }),
    selectedColorName: selectedColor.value?.name || '',
    featureFlags: {
      isMasterProduct: isMasterProductFlag,
      isBeautyProduct: beautyFlag,
      isHomeFOB: homeFOBFlag,
      isConsolidatedThirdVariant: isConsolidatedThirdVariantFlag,
      isConsolidatedPage,
    },
  }));

  // Checking outfitsAvailability to prevent Stylitics widget from attempting to load
  // if we already know above the fold whether the CTA should show or not.
  const hasOutfitsAvailable = computed(() => store.state.outfittingStore.outfitsAvailability);

  // validating all required data to display Outfitting widget
  const enableOutfittingWidget = computed(() => killswitches.enableOutfitting && hasOutfitsAvailable.value && productData);

  // isFirstMount, tells us whether the PQV Slideout wrapper is already mount or not,
  // so we don't attempt to load the main PQV content within too early, by trigerring
  // [slideoutStore/TOGGLE]. All that is being handled in productQuickviewStore.
  const enableProductQuickview = computed(() => enableOutfittingWidget.value
    && store.state.productQuickviewStore.isFirstMount);

  return {
    outfittingProps,
    enableProductQuickview,
    enableOutfittingWidget,
  };
}

export default {
  useOutfittingData,
};
